import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import posed from 'react-pose';
import breakpoint from 'styled-components-breakpoint';

import { useLocalizedFaqs } from '../utils';
import Chevron from '../assets/images/chevron-coral.svg';

import Grid from './Grid';
import Cell from './Cell';
import Block from './Block';
import H4 from './H4';
import RichTextHtml from './RichTextHtml';

const Expandable = posed.div({
  closed: { height: 0, opacity: 0 },
  open: { height: 'auto', opacity: 1 },
});

const Content = styled(Expandable)`
  overflow: hidden;

  ${breakpoint('tablet')`
    width: 60%;
  `};
`;

const AccordionButton = styled.button`
  display: flex;
  align-items: flex-start;
  text-align: left;
  width: 100%;

  border: 0;
  background-color: transparent;

  cursor: pointer;
  outline: none;
`;

const ChevronIcon = styled.img`
  width: auto;
  height: ${({ theme }) => theme.spacing.baseUp1};
  margin-right: ${({ theme }) => theme.spacing.baseDown1};
  transition: transform 0.1s;

  ${({ isOpen }) => (isOpen ? 'transform: rotate(90deg);' : '')};
`;

const AccordionItem = ({ item, isOpen, onClick }) => (
  <Block py="baseUnit">
    <AccordionButton onClick={onClick}>
      <ChevronIcon src={Chevron} alt="arrow" isOpen={isOpen} />
      <H4 color="baseFont">{item.question.text}</H4>
    </AccordionButton>
    <Content pose={isOpen ? 'open' : 'closed'}>
      <Block py="baseUnit" px={{ tablet: 'baseUp3' }}>
        <RichTextHtml html={item.answer.html} />
      </Block>
    </Content>
  </Block>
);

AccordionItem.propTypes = {
  item: PropTypes.shape({
    question: PropTypes.shape({ text: PropTypes.string }),
    answer: PropTypes.shape({ html: PropTypes.string }),
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const FaqList = ({ selectedLocale }) => {
  const faqs = useLocalizedFaqs(selectedLocale);
  const [activeUid, setActiveState] = useState('');

  const toggleAccordion = uid => {
    setActiveState(activeUid === uid ? '' : uid);
  };

  return (
    <Grid>
      <Cell size={1}>
        {faqs.map(({ node: { uid, data: faq } }) => (
          <AccordionItem
            key={`faq-item-${uid}`}
            item={faq}
            isOpen={activeUid === uid}
            onClick={() => toggleAccordion(uid)}
          />
        ))}
      </Cell>
    </Grid>
  );
};

FaqList.propTypes = {
  selectedLocale: PropTypes.string.isRequired,
};

export default FaqList;

export const query = graphql`
  fragment FaqDataFragment on PrismicFaqDataType {
    question {
      text
    }
    answer {
      html
    }
  }
`;
